import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateReport, updateReportStatus } from "../api/report.api";
import {
  Report,
  ReportStatus,
  UpdateReportPayload,
} from "../api/models/report.models";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useNavigate } from "react-router";

const useApproveReports = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sendingReports, setSendingReports] = useState<boolean>(false);
  const [bearerToken, companyId, organizationId, toast] = useUIStore(
    useShallow((state: UIState) => [
      state.bearerToken,
      state.companyId,
      state.organizationId,
      state.toastRef,
    ])
  );

  const queryClient = useQueryClient();

  const approveAndSendReports = (reports: Report[]) => {
    setSendingReports(true);
    const approveAndSendMutations = reports.map((report) => {
      const result = updateExistingReportMutation.mutateAsync({
        ...report,
        reportStatus: ReportStatus.Sent,
      });
      return result;
    });

    const result = Promise.all(approveAndSendMutations);

    result.finally(() => {
      setSendingReports(false);
      queryClient.invalidateQueries({
        queryKey: ["reports"],
      });
      navigate("/reporting?showSentReports=true");
    });

    return result;
  };

  const updateExistingReportMutation = useMutation({
    mutationFn: (payload: UpdateReportPayload) =>
      updateReportStatus(
        companyId as number,
        organizationId ?? undefined,
        payload,
        bearerToken
      ),
    onError: (error) => {
      console.error("Failed to update report row:", error);
      toast?.current?.show({
        severity: "error",
        summary: t("header-row-error"),
        detail: t("text-failed-to-update"),
        life: 3000,
      });
    },
  });

  return {
    approveAndSendReports,
    isApproving: sendingReports || updateExistingReportMutation.isPending,
  };
};

export default useApproveReports;
